import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import { Switch } from "@/components/ui/switch";
import { Heading, Text } from "@/components/ui/typography";
import { cn } from "@/lib/utils";

export type TemplatePreviewField = {
  id?: string;
  label?: string;
  type?: string;
  required?: boolean;
  enabled?: boolean;
  role?: string;
  suggestedHazards?: string[];
  controlLibrary?: string[];
};

export type TemplatePreviewSection = {
  id?: string;
  title?: string;
  enabled?: boolean;
  fields?: TemplatePreviewField[];
};

export type TemplatePreviewBlocks = {
  sections?: TemplatePreviewSection[];
};

type TemplateSectionPreviewProps = {
  blocks: TemplatePreviewBlocks | null | undefined;
  editable?: boolean;
  onSectionToggle?: (sectionId: string, enabled: boolean) => void;
  onFieldToggle?: (
    sectionId: string,
    fieldId: string,
    enabled: boolean
  ) => void;
};

function FieldDetails({ field }: { field: TemplatePreviewField }) {
  if (field.type === "signature" && field.role) {
    return (
      <Text
        variant="small"
        className="col-span-full rounded-sm bg-muted/50 px-3 py-2"
      >
        <span className="font-medium text-foreground">Role:</span> {field.role}
      </Text>
    );
  }

  if (field.type !== "hazard_list") {
    return null;
  }

  const hazards = field.suggestedHazards?.slice(0, 3) ?? [];
  const controls = field.controlLibrary?.slice(0, 3) ?? [];

  return (
    <div className="col-span-full space-y-1 rounded-sm bg-muted/50 px-3 py-2">
      {hazards.length > 0 ? (
        <Text variant="small">
          <span className="font-medium text-foreground">Hazards:</span>{" "}
          {hazards.join(", ")}
        </Text>
      ) : (
        <Text variant="small">No hazards or controls defined in template</Text>
      )}
      {controls.length > 0 ? (
        <Text variant="small">
          <span className="font-medium text-foreground">Controls:</span>{" "}
          {controls.join(", ")}
        </Text>
      ) : null}
    </div>
  );
}

export function TemplateSectionPreview({
  blocks,
  editable = false,
  onSectionToggle,
  onFieldToggle,
}: TemplateSectionPreviewProps) {
  const sections = Array.isArray(blocks?.sections) ? blocks.sections : [];

  if (sections.length === 0) {
    return (
      <Empty>
        <EmptyHeader>
          <EmptyTitle>No schema blocks</EmptyTitle>
          <EmptyDescription>
            This template does not define any sections yet.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    );
  }

  return (
    <div className="space-y-3">
      {sections.map((section, sectionIndex) => {
        const sectionId = section.id ?? `section-${sectionIndex}`;
        const sectionTitle = section.title ?? "Untitled section";
        const fields = Array.isArray(section.fields) ? section.fields : [];
        const hasRequiredFields = fields.some((field) => field.required);
        const sectionEnabled = section.enabled !== false;

        return (
          <section key={sectionId} aria-label={sectionTitle}>
            <Card
              className={cn(
                "gap-0 overflow-hidden py-0",
                !sectionEnabled && "opacity-60"
              )}
            >
              <div className="flex min-h-12 flex-wrap items-center justify-between gap-3 px-4 py-3">
                <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-1">
                  <Heading variant="subsection" className="truncate">
                    {sectionTitle}
                  </Heading>
                  <Text as="span" variant="caption">
                    {fields.length} field{fields.length === 1 ? "" : "s"}
                  </Text>
                </div>
                {hasRequiredFields ? (
                  <Badge variant="required" size="xs">
                    Required
                  </Badge>
                ) : editable ? (
                  <Switch
                    aria-label={`Include ${sectionTitle} section`}
                    checked={sectionEnabled}
                    onCheckedChange={(checked) =>
                      onSectionToggle?.(sectionId, checked)
                    }
                  />
                ) : null}
              </div>

              {fields.length === 0 ? (
                <Text
                  variant="small"
                  className="border-t border-border/60 px-4 py-3"
                >
                  No fields configured for this section.
                </Text>
              ) : (
                <ul className="divide-y divide-border/60 border-t border-border/60">
                  {fields.map((field, fieldIndex) => {
                    const fieldId = field.id ?? `field-${fieldIndex}`;
                    const fieldLabel =
                      field.label ?? field.id ?? "Untitled field";
                    const fieldEnabled = field.enabled !== false;

                    return (
                      <li
                        key={fieldId}
                        data-slot="template-preview-field"
                        className={cn(
                          "grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-4 py-2",
                          !fieldEnabled && "opacity-60"
                        )}
                      >
                        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-1">
                          <Text as="span" variant="label" className="truncate">
                            {fieldLabel}
                          </Text>
                          <Text
                            as="span"
                            variant="caption"
                            className="font-meta uppercase"
                          >
                            {field.type ?? "unknown"}
                          </Text>
                        </div>
                        {field.required ? (
                          <Badge variant="required" size="xs">
                            Required
                          </Badge>
                        ) : editable ? (
                          <Switch
                            aria-label={`Include ${fieldLabel} field`}
                            checked={fieldEnabled}
                            onCheckedChange={(checked) =>
                              onFieldToggle?.(sectionId, fieldId, checked)
                            }
                          />
                        ) : null}
                        <FieldDetails field={field} />
                      </li>
                    );
                  })}
                </ul>
              )}
            </Card>
          </section>
        );
      })}
    </div>
  );
}
